import React from 'react';

import { observer } from 'mobx-react';

import TabPanel from 'components/CustomTabPanel/CustomTabPanel';

import { Button } from 'components/Button';
import CustomModal from 'components/CustomModal';
import { EntityModules, UserRole } from 'core/constants';
import NavigationBlocker from 'components/NavigationBlocker';
import { DocumentAccessLevel } from 'utils/api/modules/entities/entities.types';

import { useEditorStore } from 'containers/Tiptap/hooks/useEditorStore';

import { getEntityTranslation } from 'stores/EntityDetails/utils';

import { useStore } from 'utils/hooks/store';

import { StyledTab, StyledTabs, TabsContainer } from './styles';
import { EntityDetailsContentProps, TabConfig } from './types';
import { ENTITY_TABS_CONFIG, DEFAULT_TABS } from './config';

const SETTINGS_TAB_ID = 'document-settings-tab';
const DOCUMENT_TAB_ID = 'documents-tab';

// Lets the Settings panel fill the card height so its sticky action bar pins to the bottom
const FILL_PANEL_STYLE: React.CSSProperties = { flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' };

const EntityDetailsContent: React.FC<EntityDetailsContentProps> = ({ onError, store }) => {
  const [hideEmptyAnswers, setHideEmptyAnswers] = React.useState(false);
  const [pendingTabIndex, setPendingTabIndex] = React.useState<number | null>(null);
  const { session } = useStore();
  const editorStore = useEditorStore();
  const userRole = session.user?.role;

  const allTabs: TabConfig[] = ENTITY_TABS_CONFIG[store.entityType] || DEFAULT_TABS || [];
  const tabs = allTabs.filter((tab) => {
    if (tab.requiredRoles && !tab.requiredRoles.includes(userRole as UserRole)) return false;
    if (tab.requiredFlag && !session.user?.[tab.requiredFlag]) return false;
    if (tab.hiddenByFlag && session.user?.[tab.hiddenByFlag]) return false;
    if (tab.requiredEditAccess && !('accessLevel' in store && store.accessLevel !== DocumentAccessLevel.View)) {
      return false;
    }
    return true;
  });

  const [value, setValue] = React.useState(() => {
    const linked = new URLSearchParams(window.location.search).get('tab');
    return Math.max(
      tabs.findIndex((tab) => tab.id === linked),
      0
    );
  });

  // The audit record has no outer card: the Answers tab's section cards are the cards.
  const flat = store.entityType === EntityModules.Audits;

  const currentTabId = tabs[value]?.id;
  const leavingSettings = currentTabId === SETTINGS_TAB_ID && editorStore.isSettingsDirty;

  const switchTab = (newValue: number): void => {
    if (tabs[newValue]?.id === DOCUMENT_TAB_ID && currentTabId !== DOCUMENT_TAB_ID) {
      editorStore.bumpDocumentTabReflowTick();
    }
    // Close any open header/footer overlay before navigating away from the Document tab.
    if (editorStore.editor) {
      const cmds = editorStore.editor.commands as Record<string, unknown>;
      if (typeof cmds.closeHeaderFooterEditors === 'function') {
        editorStore.editor.commands.closeHeaderFooterEditors();
      }
    }
    setValue(newValue);
  };

  const handleChange = (_: React.SyntheticEvent, newValue: number): void => {
    if (leavingSettings && tabs[newValue]?.id !== SETTINGS_TAB_ID) {
      setPendingTabIndex(newValue);
      return;
    }
    switchTab(newValue);
  };

  const confirmTabChange = (): void => {
    if (pendingTabIndex == null) return;
    editorStore.setSettingsDirty(false);
    switchTab(pendingTabIndex);
    setPendingTabIndex(null);
  };

  const cancelTabChange = (): void => {
    setPendingTabIndex(null);
  };

  const entitySingular = getEntityTranslation[store.entityType].singularKey;
  const blockerWhen = store.isContentEdited || editorStore.isSettingsDirty;
  const navigateMessage = store.isContentEdited
    ? `You have made updates in the ${entitySingular} record, but you haven't saved the changes. Do you want to discard these changes?`
    : 'You have unsaved changes in Settings. Do you want to discard these changes?';

  return (
    <>
      <NavigationBlocker
        when={blockerWhen}
        // Read live: a caller can clear the flag and navigate before this component re-renders.
        shouldBlockNavigation={() => store.isContentEdited || editorStore.isSettingsDirty}
        navigateMessage={navigateMessage}
      />
      <CustomModal
        modalWidth="sm"
        openModal={pendingTabIndex != null}
        onClose={cancelTabChange}
        modalTitle="Unsaved changes"
        modalDescription="You have unsaved changes in Settings. Switch tabs and discard them?"
        cancelButton={
          <Button variant="outlined" size="medium" onClick={cancelTabChange}>
            No, keep editing
          </Button>
        }
        submitButton={
          <Button variant="contained" color="error" size="medium" onClick={confirmTabChange}>
            Yes, discard changes
          </Button>
        }
      />
      <TabsContainer $flat={flat}>
        <StyledTabs $flat={flat} value={value} onChange={handleChange} variant="scrollable" scrollButtons={false}>
          {tabs.map((tab: TabConfig) => (
            <StyledTab key={tab.id} label={tab.label} id={tab.id} data-testid={tab.testId} />
          ))}
        </StyledTabs>
        {tabs.map((tab: TabConfig, index: number) => (
          <TabPanel
            key={tab.id}
            value={value}
            index={index}
            keepMounted={tab.keepMounted}
            customStyle={tab.id === SETTINGS_TAB_ID ? FILL_PANEL_STYLE : undefined}
          >
            <tab.component
              store={store}
              onError={onError}
              hideEmptyAnswers={hideEmptyAnswers}
              setHideEmptyAnswers={setHideEmptyAnswers}
            />
          </TabPanel>
        ))}
      </TabsContainer>
    </>
  );
};

export default observer(EntityDetailsContent);
